/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-summary/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ISwitchMenu } from '@interfaces/svt.interface';
import cn from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import LegendIcons from '@components/central-monitoring/legend-icons/legend-icons';
import ReviewHistoryGrid from '@components/central-monitoring/study-summary/review-history-grid';
import RiskOverviewScrollableGrid from '@components/central-monitoring/study-summary/risk-overview-scrollable-grid';
import SiteOverviewScrollableGrid from '@components/central-monitoring/study-summary/site-overview-scrollable-grid';
import StudyOverviewScrollableGrid from '@components/central-monitoring/study-summary/study-overview-scrollable-grid';
import { STUDY_SUMMARY_GRIDS } from '@components/central-monitoring/study-summary/study-summary.data';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import { IGridViewMenuItem } from '@components/core/expandable/grid-v2/grid-view-menu/grid-view-menu-props.interface';
import OpraReview from '@components/core/opra-review';
import OpraTabs from '@components/core/opra-tabs';
import { IOpraTab } from '@components/core/opra-tabs/opra-tabs.interface';
import SelectWithIcons from '@components/core/select-with-icons';
import StatisticsBar from '@components/core/statistics-bar';
import TwoColsSplitter from '@components/core/two-cols-splitter/index';
import { ViewOption } from '@components/core/two-cols-splitter/two-cols-splitter.interface';
import ControlObservation from '../control-observation';
import ObservationsSectionV2 from './observations-section-v2';
import { formReviewData, formSummaryData, getStudySummaryInitialGrid } from './study-summary.helper';
import { useBoolean, useTranslation } from 'hooks';
import { GET_CM_OBSERVATIONS_AND_ACTIONS } from 'queries/central-monitoring';
import { calculateFieldsForStudySummary } from 'utils/cm-review-report';
import { findAtLeastOneTrueValue } from 'utils/shared';
import Loading from 'views/components/core/loading';
import Error from 'views/components/error/error';
import './styles.scss';
import Constants from '@utils/constants';
import { DataSetFields } from '@queries/generated/fragments.generated';

const StudySummary = ({
    studyData,
    config,
    datasetDate,
    siteScores,
    onOpenEditReview,
    onOpenObservationsModal,
    onOpenActionModal,
    study,
    show,
    canEdit,
    reportData,
    datasetDates,
    dataSets,
    dataSetReviews,
    sitesStatusesToExclude,
    owners,
    currentGrid,
    setCurrentGrid,
    activeTab,
    setActiveTab,
}: any) => {
    const { t } = useTranslation();
    const reviewRef = useRef<HTMLDivElement>(null);
    const reviewGridRef = useRef<HTMLDivElement>(null);

    const formattedDataSetDates =
        datasetDates?.filter(
            (set: any) =>
                set.isDataset && datasetDate && new Date(datasetDate._date).getTime() > new Date(set._date).getTime(),
        ) ?? [];

    const defaultDataSet = {
        ...(formattedDataSetDates[0] ?? {}),
        _dataCutDate: t('admin:selectValue'),
        id: Constants.ObjectId.Empty,
        isDefault: true,
    };
    formattedDataSetDates.unshift(defaultDataSet);

    const [studySummary, setStudySummary] = useState({} as any);
    const [studyConfig, setStudyConfig] = useState({} as any);
    const [currentView, setCurrentView] = useState(ViewOption.LEFT);
    const [comparisonDate, setComparisonDate] = useState<DataSetFields>(defaultDataSet);

    const showSummarySection = useBoolean(false);

    const { data, loading, error } = useQuery(GET_CM_OBSERVATIONS_AND_ACTIONS, {
        variables: {
            studyId: study?.id || Constants.ObjectId.Empty,
            ...(datasetDate?.isCMR ? { timestamp: datasetDate.created } : {}),
        },
    });

    const summaryData = formSummaryData(studyConfig, studySummary, datasetDate);
    const gridAvailable =
        study?.displayRiskOverviewOnStudySummary ||
        study?.displayStudyOverviewOnStudySummary ||
        study?.displayStudySiteOverviewOnStudySummary;

    useEffect(() => {
        if (studyData) {
            setStudyConfig(config?.studySummary);
            if (studyData.sites) {
                setStudySummary(calculateFieldsForStudySummary(studyData.sites));
            }
        }
    }, [studyData, sitesStatusesToExclude, siteScores, reportData]);

    useEffect(() => {
        // Reset comparison dataset when dataset changed
        if (comparisonDate) {
            setComparisonDate(formattedDataSetDates[0]);
        }
    }, [datasetDate]);

    useEffect(() => {
        if (studyConfig) {
            showSummarySection.setValue(findAtLeastOneTrueValue(studyConfig) || datasetDate?.isCMR);
        }
    }, [studyConfig, datasetDate, reportData, show]);

    useEffect(() => {
        if (study) setCurrentGrid(getStudySummaryInitialGrid(study));
    }, [study]);

    const gridViewOptions: IGridViewMenuItem[] = [
        {
            label: 'ra:fullView',
            onClick: () => {
                setCurrentView(ViewOption.FULL);
            },
            icon: ['fal', 'square'],
            enabled: true,
            customIcon: (
                <div className="view-box-outer menu-icon">
                    <div className="view-box-inner" />
                </div>
            ),
            isActive: currentView === ViewOption.FULL,
        },
        {
            label: 'ra:leftView',
            onClick: () => {
                setCurrentView(ViewOption.LEFT);
            },
            icon: ['fal', 'square'],
            enabled: true,
            customIcon: (
                <div className="view-box-outer menu-icon">
                    <div className="view-box-inner view-box-inner--left" />
                </div>
            ),
            isActive: currentView === ViewOption.LEFT,
        },
        {
            label: 'ra:topView',
            onClick: () => {
                setCurrentView(ViewOption.TOP);
            },
            icon: ['fal', 'square'],
            enabled: true,
            customIcon: (
                <div className="view-box-outer menu-icon">
                    <div className="view-box-inner view-box-inner--top" />
                </div>
            ),
            isActive: currentView === ViewOption.TOP,
        },
    ];

    const switchMenuItems = () => {
        const menuItems: ISwitchMenu[] = [];
        if (study?.displayRiskOverviewOnStudySummary) {
            menuItems.push({
                label: t('admin:riskOverview'),
                value: STUDY_SUMMARY_GRIDS.risk,
                onClick: (value: string) => {
                    setCurrentGrid(value);
                },
                currentValue: currentGrid,
            });
        }
        if (study?.displayStudyOverviewOnStudySummary) {
            menuItems.push({
                label: t('admin:studyOverview'),
                value: STUDY_SUMMARY_GRIDS.study,
                onClick: (value: string) => {
                    setCurrentGrid(value);
                },
                currentValue: currentGrid,
            });
        }
        if (study?.displayStudySiteOverviewOnStudySummary) {
            menuItems.push({
                label: t('admin:siteOverview'),
                value: STUDY_SUMMARY_GRIDS.site,
                onClick: (value: string) => {
                    setCurrentGrid(value);
                },
                currentValue: currentGrid,
            });
        }

        return menuItems;
    };

    const getGrid = () => {
        if (currentGrid === STUDY_SUMMARY_GRIDS.risk) {
            return (
                <RiskOverviewScrollableGrid
                    studyData={studyData}
                    config={config}
                    sitesStatusesToExclude={sitesStatusesToExclude}
                    isExcludeSites={false}
                    gridViewOptions={gridViewOptions}
                    siteScores={siteScores}
                    reportData={reportData}
                    switchMenu={switchMenuItems()}
                    inactive={!gridAvailable}
                />
            );
        }

        if (currentGrid === STUDY_SUMMARY_GRIDS.study) {
            return (
                <StudyOverviewScrollableGrid
                    studyData={studyData}
                    studyId={study?.id}
                    currentDataSet={datasetDate}
                    previousDataSet={comparisonDate}
                    switchMenu={switchMenuItems()}
                    gridViewOptions={gridViewOptions}
                    inactive={!gridAvailable}
                />
            );
        }

        if (currentGrid === STUDY_SUMMARY_GRIDS.site) {
            return (
                <SiteOverviewScrollableGrid
                    studyData={studyData}
                    sitesStatusesToExclude={sitesStatusesToExclude}
                    gridViewOptions={gridViewOptions}
                    switchMenu={switchMenuItems()}
                    currentDataSet={datasetDate}
                    previousDataSet={comparisonDate}
                    studyId={study?.id}
                    inactive={!gridAvailable}
                />
            );
        }
    };

    const changeReviewsGridHeight = () => {
        if (reviewRef?.current && reviewGridRef?.current) {
            reviewGridRef.current.style.height = `calc(100% - ${reviewRef.current.clientHeight + 9}px)`;
        }
    };

    const tabsData: IOpraTab[] = [
        {
            tabTitle: t('ra:observationsActions'),
            tabContent: (
                <ControlObservation studyId={study?.id} datasetDate={datasetDate}>
                    {(handleDeleteObs: Function, handleRestoreObs: Function) => (
                        <ObservationsSectionV2
                            onOpenObservationsModal={onOpenObservationsModal}
                            onOpenActionModal={onOpenActionModal}
                            study={study}
                            canEdit={canEdit}
                            datasetDate={datasetDate}
                            data={data}
                            ownerOptions={owners}
                            datasetDates={datasetDates}
                            studyData={studyData}
                            handleDeleteObs={handleDeleteObs}
                            handleRestoreObs={handleRestoreObs}
                        />
                    )}
                </ControlObservation>
            ),
        },
        {
            tabTitle: t('cm:studyReview'),
            tabContent: !!reportData?.centralMonitoringReviewData?.studyReview && (
                <div className="review-section-wrap">
                    <div ref={reviewRef} className="review-wrap">
                        <OpraReview
                            datasetDate={datasetDate?._dataCutDate}
                            action={
                                <DarkTooltip title={t('cm:editReview')} placement="top" arrow>
                                    <div>
                                        <button
                                            onClick={() => onOpenEditReview()}
                                            disabled={!canEdit || !datasetDate?.isDataset}
                                            type="button"
                                            className="single-action-button"
                                            style={!canEdit || !datasetDate?.isDataset ? { pointerEvents: 'none' } : {}}
                                            data-testid="edit-review"
                                        >
                                            <FontAwesomeIcon icon={['fal', 'edit']} className="menu-icon" />
                                        </button>
                                    </div>
                                </DarkTooltip>
                            }
                            data={formReviewData(reportData.centralMonitoringReviewData.studyReview)}
                            onInit={changeReviewsGridHeight}
                        />
                    </div>
                    <div ref={reviewGridRef} className="review-grid-wrap">
                        <ReviewHistoryGrid
                            dataSets={dataSets}
                            dataSetReviews={dataSetReviews}
                            selectedDataset={datasetDate}
                        />
                    </div>
                </div>
            ),
        },
    ];

    if (loading) return <Loading float />;

    if (error) return <Error error={error} />;

    if (!loading && !studyData) return <h1>No data available</h1>;

    return (
        <>
            {showSummarySection.value && <StatisticsBar data={summaryData} className="study-summary-statistics" />}
            <div className="study-summary-legend-wrap">
                <div className="comparison-date">
                    <span className="comparison-date__label">Comparison Dataset:</span>
                    <SelectWithIcons
                        items={formattedDataSetDates}
                        fieldName="_dataCutDate"
                        onChange={(event) => {
                            setComparisonDate(event.target.value || null);
                        }}
                        width={122}
                        selectedValue={comparisonDate}
                        className={cn({ 'default-value': comparisonDate.isDefault })}
                        popupClassName="comparison-date__popup"
                        disabled={datasetDates.length === 1}
                        data-testid="comparison-date"
                    />
                </div>
                <LegendIcons withLabel />
            </div>
            <div className="study-summary-grids-wrapper">
                {currentView === ViewOption.FULL ? (
                    getGrid()
                ) : (
                    <TwoColsSplitter
                        orientation={currentView === ViewOption.LEFT ? 'horizontal' : 'vertical'}
                        alignmentValues={[
                            {
                                min: '30%',
                                size: '50%',
                                keepMounted: true,
                            },
                            { min: '30%' },
                        ]}
                        onResize={changeReviewsGridHeight}
                        leftCol={<>{getGrid()}</>}
                        rightCol={<OpraTabs data={tabsData} activeTab={activeTab} setActiveTab={setActiveTab} />}
                    />
                )}
            </div>
        </>
    );
};

export default StudySummary;
